import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

function BsIcon({ name, size = 18 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':   return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'search': return <svg {...p}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
        case 'gang':   return <svg {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
        case 'check':  return <svg {...p}><polyline points="20 6 9 17 4 12"/></svg>;
        default: return null;
    }
}

export default function Buscar() {
    const { t } = useTranslation();
    const [winegangData, setWinegangData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [userRequest, setRequestData] = useState({ invitations: [], demands: [], winegangUsers: [] });
    const [profileInfo, setProfileInfo] = useState({ id: 0 });
    const [sentIds, setSentIds] = useState(new Set());
    const [ready, setReady] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const navigate = useNavigate();

    useEffect(() => { requestAnimationFrame(() => setReady(true)); }, []);

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userLogged');
        if (storedUserInfo) {
            const userInfo = JSON.parse(storedUserInfo);
            setProfileInfo({ id: userInfo.id || 0 });
        }
    }, []);

    const fetchWinegangData = () => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/winegangs`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    setWinegangData(JSON.parse(this.responseText));
                } else {
                    setFetchError(true);
                }
            }
        };
        xhr.send();
    };

    const fetchRequestData = () => {
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/findByUserId/${profileInfo.id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                setRequestData(JSON.parse(this.responseText));
            }
        };
        xhr.send();
    };

    useEffect(() => {
        if (profileInfo && profileInfo.id) {
            fetchRequestData();
            fetchWinegangData();
        }
    }, [profileInfo, retryKey]);

    const createDemand = (winegangId) => {
        try {
            const xhr = new XMLHttpRequest();
            const data = new FormData();
            data.append('user_id', profileInfo.id);
            data.append('winegang_id', winegangId);
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/demand`, true);
            xhr.onreadystatechange = function () {
                if (this.readyState === 4 && this.status === 201) {
                    setSentIds(prev => new Set([...prev, winegangId]));
                    fetchRequestData();
                }
            };
            xhr.send(data);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const filteredWinegangs = winegangData.filter(winegang => {
        const winegangId = Number(winegang.id);
        const isUser = userRequest.winegangUsers.some(wu => Number(wu.winegang_id) === winegangId);
        const hasInvitation = userRequest.invitations.some(inv => Number(inv.winegang_id) === winegangId);
        const hasDemand = userRequest.demands.some(d => Number(d.winegang_id) === winegangId);
        return !isUser && !hasInvitation && !hasDemand &&
            winegang.winegang_name.toLowerCase().includes(searchText.toLowerCase());
    });

    return (
        <div className={`bs-stage${ready ? ' bs-ready' : ''}`}>
            <div className="bs-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="bs-vignette" />
            <div className="bs-grain" />

            <div className="bs-header">
                <button className="bs-icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <BsIcon name="back" size={16} />
                </button>
                <span className="bs-title">{t('gangs.search_title')}</span>
                <div style={{ width: 40 }} />
            </div>

            <div className="bs-search-wrap">
                <div className="bs-search-field">
                    <BsIcon name="search" size={15} />
                    <input
                        className="bs-search-input"
                        type="text"
                        placeholder={t('gangs.placeholder_search')}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            {filteredWinegangs.length > 0 && (
                <div className="bs-count">{filteredWinegangs.length} {t('gangs.results')}</div>
            )}

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>
                        {t('common.retry')}
                    </button>
                </div>
            )}

            <div className="bs-list">
                {filteredWinegangs.length === 0 ? (
                    <div className="bs-empty">{searchText ? t('gangs.no_results') : t('gangs.empty_search')}</div>
                ) : (
                    filteredWinegangs.map((winegang) => {
                        const isSent = sentIds.has(winegang.id);
                        return (
                            <div key={winegang.id} className="bs-card">
                                <div className="bs-card-icon">
                                    <BsIcon name="gang" size={16} />
                                </div>
                                <div className="bs-card-info">
                                    <div
                                        className="bs-card-name"
                                        onClick={() => navigate(`/gangs/${winegang.id}`)}
                                    >
                                        {winegang.winegang_name}
                                    </div>
                                    <div className="bs-card-meta">
                                        {winegang.winegang_users_count} {t('common.members')} · {winegang.average?.toFixed(2)}%
                                    </div>
                                </div>
                                <button
                                    className={`bs-request-btn${isSent ? ' bs-sent' : ''}`}
                                    onClick={() => !isSent && createDemand(winegang.id)}
                                    disabled={isSent}
                                >
                                    {isSent
                                        ? <BsIcon name="check" size={13} />
                                        : t('gangs.col_request')}
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
